/**
 * L'arborescence de navigation du CMS, source unique.
 *
 * Deux écrans la consomment : la barre latérale (`AdminSidebar.vue`) et la
 * palette de commandes (`CommandPalette.vue`). Les tenir séparées les ferait
 * diverger — une entrée ajoutée au menu manquerait à la palette, sans que rien
 * ne le signale.
 *
 * Le filtre par rôle vit ici pour la même raison : recopié dans la palette, il
 * finirait par ne plus correspondre à celui du menu, et la palette proposerait
 * des destinations que l'écran refuserait en 403.
 */

import { menuLocationLabels, menuLocationOrder } from '~~/shared/utils/enums'
import { cleLibelleSection, estUrlExterne, PREFIXE_PAGE } from '~~/shared/utils/menu-administration'
import type { ElementCree, MenuAdministrationConfig, PageSystemeMenu } from '~~/shared/utils/menu-administration'

// --- Types ---

/**
 * Droits requis par une entrée, partagés par tous les nœuds de l'arbre.
 *
 * Un nœud intermédiaire qui les porte les transmet à toute sa descendance :
 * masquer « Menus » sans masquer ses zones laisserait des destinations
 * orphelines dans la palette.
 */
export interface NavRoles {
  /** Entrée de maintenance, masquée aux rôles non administrateurs */
  adminOnly?: boolean
  /**
   * Entrée réservée aux superadministrateurs de l'annuaire.
   *
   * Plus étroit qu'`adminOnly` : le rôle porté par cette seule application ne
   * suffit pas. Réservé aux sauvegardes.
   */
  superAdminOnly?: boolean
  /**
   * Entrée réservée aux superadministrateurs, au sens de `requireSuperAdmin`.
   *
   * Entre les deux précédents : le rôle superadmin de cette application suffit,
   * contrairement à `superAdminOnly`. Suit ce que vérifient les routes des
   * seeds, qui refont la structure du site sans être irréversibles comme une
   * restauration de sauvegarde.
   */
  appSuperAdminOnly?: boolean
}

/** Ce que tout nœud porte, quelle que soit sa nature. */
interface NavBase extends NavRoles {
  id: string
  label: string
  icon: string
}

/** Feuille : une destination, et rien en dessous. */
export interface NavLeaf extends NavBase {
  to: string
  children?: undefined
  /** Lien libre vers une autre adresse que l'administration (#343). */
  externe?: boolean
  /** Lien libre à ouvrir dans un nouvel onglet (#343). */
  nouvelOnglet?: boolean
}

/**
 * Branche : un chemin vers des destinations.
 *
 * `to` y est facultatif, et c'est le seul cas où un nœud est les deux à la
 * fois. « Menus » l'illustre : la liste des zones est un écran à part entière,
 * et chaque zone en est une aussi. Sans `to`, la branche n'est qu'un chemin —
 * proposer « Législation » dans la palette donnerait une ligne sur laquelle
 * Entrée ne mènerait nulle part. Son libellé vaut alors comme terme de
 * recherche pour ses feuilles, rien de plus.
 */
export interface NavBranch extends NavBase {
  to?: string
  children: NavNode[]
  /**
   * Les enfants existent pour la palette, pas pour la barre latérale.
   *
   * Le cas des zones de menu : les proposer dans la palette fait gagner deux
   * clics, mais les déplier dans le menu allongerait une colonne déjà longue
   * pour un écran qui les liste déjà. Sans ce drapeau, ajouter des enfants à
   * une entrée jusque-là simple la transformerait en sous-menu dépliable —
   * une régression du menu payée pour une commodité de la palette.
   */
  enfantsHorsMenu?: boolean
}

/**
 * Un nœud est une feuille ou une branche, et les enfants d'une branche sont des
 * nœuds de même nature : la profondeur n'est pas bornée.
 *
 * C'est la différence avec la structure d'origine, dont le typage enfermait le
 * menu à deux niveaux (`children: { label, to, icon }[]`). Le menu gagnera des
 * niveaux ; une palette qui n'en gérerait que deux cesserait de les trouver, en
 * silence.
 */
export type NavNode = NavLeaf | NavBranch

export function isNavBranch(node: NavNode): node is NavBranch {
  return Array.isArray(node.children)
}

/** Un groupe de premier niveau, tel que la barre latérale les empile. */
export interface NavGroup {
  key: string
  label: string
  /** Clé d'accent, résolue en classes par la barre latérale */
  accent: 'primary' | 'violet'
  items: NavNode[]
}

/** Les droits de la session, tels que le store d'authentification les expose. */
export interface NavCapabilities {
  isAdmin: boolean
  isSuperAdmin: boolean
  isGlobalSuperAdmin: boolean
}

// --- L'arborescence ---

/**
 * Les zones de menu, en destinations.
 *
 * Elles sont statiques — `menuLocations` les fige dans `shared/utils/enums.ts`
 * — donc éligibles à la palette sans appel réseau. Les groupes de réglages
 * (`/parametres/[group]`), eux, viennent de l'API : les inclure imposerait une
 * requête à l'ouverture et une liste qui change selon l'état de la base.
 *
 * `adminOnly` est porté par le parent : les quatre zones en héritent.
 */
const zonesDeMenu: NavLeaf[] = menuLocationOrder.map(location => ({
  id: `menu-${location}`,
  label: menuLocationLabels[location],
  icon: 'i-lucide-list',
  to: `/menus/${location}`,
}))

export const navGroups: NavGroup[] = [
  {
    key: 'site',
    label: 'Site',
    accent: 'primary',
    items: [
      { id: 'homepage', label: 'Page d\'accueil', icon: 'i-lucide-home', to: '/pages/accueil' },
      { id: 'qui-sommes-nous', label: 'Qui sommes-nous', icon: 'i-lucide-info', to: '/pages/qui-sommes-nous' },
      /*
       * Page système qui présente les publics de l'association : ses cibles,
       * dont chacune a sa page sous elle, s'éditent depuis ce même groupe.
       */
      {
        id: 'qui-accompagnons-nous',
        label: 'Qui accompagnons-nous ?',
        icon: 'i-lucide-users',
        children: [
          { id: 'cibles', label: 'Cibles', to: '/cibles', icon: 'i-lucide-target' },
          { id: 'qui-accompagnons-nous-page', label: 'Éditer la page', to: '/pages/qui-accompagnons-nous', icon: 'i-lucide-pencil' },
        ],
      },
      { id: 'fnacc', label: 'FNACC', icon: 'i-lucide-building', to: '/pages/fnacc' },
      { id: 'pages', label: 'Pages', icon: 'i-lucide-file-text', to: '/pages' },
      {
        id: 'articles',
        label: 'Articles',
        icon: 'i-lucide-newspaper',
        children: [
          { id: 'articles-liste', label: 'Tous les articles', to: '/articles', icon: 'i-lucide-list' },
          { id: 'articles-page', label: 'Éditer la page', to: '/pages/articles', icon: 'i-lucide-pencil' },
        ],
      },
      {
        id: 'actualites',
        label: 'Actualités',
        icon: 'i-lucide-megaphone',
        children: [
          { id: 'actualites-liste', label: 'Toutes les actualités', to: '/actualites', icon: 'i-lucide-list' },
          { id: 'actualites-page', label: 'Éditer la page', to: '/pages/actualites', icon: 'i-lucide-pencil' },
        ],
      },
      {
        id: 'legislation',
        label: 'Législation',
        icon: 'i-lucide-scale',
        children: [
          { id: 'legislation-themes', label: 'Thèmes', to: '/legislation/themes', icon: 'i-lucide-folder-cog' },
          { id: 'legislation-fiches', label: 'Fiches', to: '/legislation', icon: 'i-lucide-list' },
          { id: 'legislation-page', label: 'Éditer la page', to: '/pages/legislation', icon: 'i-lucide-pencil' },
        ],
      },
      {
        id: 'jurisprudence',
        label: 'Jurisprudence',
        icon: 'i-lucide-gavel',
        children: [
          { id: 'jurisprudence-liste', label: 'Toutes les fiches', to: '/jurisprudence', icon: 'i-lucide-list' },
          { id: 'jurisprudence-page', label: 'Éditer la page', to: '/pages/jurisprudence', icon: 'i-lucide-pencil' },
        ],
      },
      {
        id: 'partenaires',
        label: 'Partenaires',
        icon: 'i-lucide-handshake',
        children: [
          { id: 'partenaires-liste', label: 'Tous les partenaires', to: '/partenaires', icon: 'i-lucide-list' },
          { id: 'partenaires-page', label: 'Éditer la page', to: '/pages/partenaires', icon: 'i-lucide-pencil' },
        ],
      },
      {
        id: 'presse',
        label: 'Presse',
        icon: 'i-lucide-newspaper',
        children: [
          { id: 'presse-liste', label: 'Toutes les parutions', to: '/presse', icon: 'i-lucide-list' },
          { id: 'presse-medias', label: 'Médias', to: '/presse/medias', icon: 'i-lucide-newspaper' },
          { id: 'presse-page', label: 'Éditer la page', to: '/pages/presse', icon: 'i-lucide-pencil' },
        ],
      },
      { id: 'institutionnels', label: 'Institutionnels', icon: 'i-lucide-landmark', to: '/pages/institutionnels' },
      { id: 'outils-et-services', label: 'Outils et services', icon: 'i-lucide-wrench', to: '/pages/outils-et-services' },
      { id: 'adherer', label: 'Adhérer', icon: 'i-lucide-user-plus', to: '/pages/adherer' },
      { id: 'contact', label: 'Contact', icon: 'i-lucide-mail', to: '/pages/contact' },
      { id: 'mentions-legales', label: 'Mentions légales', icon: 'i-lucide-file-check', to: '/pages/mentions-legales' },
      { id: 'politique-de-confidentialite', label: 'Politique de confidentialité', icon: 'i-lucide-shield-check', to: '/pages/politique-de-confidentialite' },
    ],
  },
  {
    key: 'adherent',
    label: 'Espace adhérent',
    accent: 'violet',
    items: [
      {
        id: 'ateliers',
        label: 'Ateliers',
        icon: 'i-lucide-presentation',
        children: [
          { id: 'ateliers-liste', label: 'Tous les ateliers', to: '/ateliers', icon: 'i-lucide-list' },
          { id: 'ateliers-page', label: 'Éditer la page', to: '/pages/ateliers', icon: 'i-lucide-pencil' },
        ],
      },
      {
        id: 'lettres',
        label: 'Lettres mensuelles',
        icon: 'i-lucide-book-open',
        children: [
          { id: 'lettres-liste', label: 'Toutes les lettres', to: '/lettres-mensuelles', icon: 'i-lucide-list' },
          { id: 'lettres-page', label: 'Éditer la page', to: '/pages/lettres-mensuelles', icon: 'i-lucide-pencil' },
        ],
      },
      {
        id: 'librairie',
        label: 'Librairie',
        icon: 'i-lucide-library',
        children: [
          { id: 'librairie-liste', label: 'Tous les documents', to: '/librairie', icon: 'i-lucide-list' },
          // Les natures de document (fiche, guide…), éditables depuis qu'elles ont quitté le code.
          { id: 'librairie-natures', label: 'Natures', to: '/librairie/natures', icon: 'i-lucide-shapes' },
          { id: 'librairie-page', label: 'Éditer la page', to: '/pages/librairie', icon: 'i-lucide-pencil' },
        ],
      },
      { id: 'connexion', label: 'Connexion', icon: 'i-lucide-log-in', to: '/pages/connexion' },
      { id: 'mot-de-passe-oublie', label: 'Mot de passe oublié', icon: 'i-lucide-key-round', to: '/pages/mot-de-passe-oublie' },
      /*
       * Journal des connexions adhérents (#319), lu chez Adherence : sécurité
       * et réquisitions. Administrateurs seulement, comme la route.
       */
      { id: 'connexions-adherents', label: 'Connexions adhérents', icon: 'i-lucide-shield-check', to: '/connexions-adherents', adminOnly: true },
    ],
  },
  {
    key: 'ressources',
    label: 'Ressources',
    accent: 'primary',
    items: [
      { id: 'medias', label: 'Médiathèque', icon: 'i-lucide-image', to: '/medias' },
      // Audience du site public (#311) : une mesure, non un contenu du site.
      { id: 'audience', label: 'Audience', icon: 'i-lucide-chart-column', to: '/audience' },
      { id: 'categories', label: 'Catégories', icon: 'i-lucide-folder-tree', to: '/categories' },
      { id: 'mots-cles', label: 'Mots-clés', icon: 'i-lucide-tag', to: '/tags' },
      { id: 'cta', label: 'Bandeaux d\'action', icon: 'i-lucide-rectangle-ellipsis', to: '/cta-templates' },
      /*
       * Les carrousels rejoignent les ressources, aux côtés des bandeaux
       * d'action : comme eux, ce sont des blocs réutilisables qu'une page
       * appelle, non des contenus servis à une adresse propre.
       */
      { id: 'carrousels', label: 'Carrousels', icon: 'i-lucide-gallery-horizontal-end', to: '/carrousels' },
      { id: 'couleurs', label: 'Couleurs du site', icon: 'i-lucide-palette', to: '/colors' },
    ],
  },
  {
    key: 'parametres',
    label: 'Paramètres',
    accent: 'primary',
    items: [
      { id: 'parametres', label: 'Réglages du site', icon: 'i-lucide-settings', to: '/parametres' },
      /*
       * « Menus » reste une destination — la liste des zones — tout en portant
       * ses quatre zones comme enfants. `enfantsHorsMenu` garde la barre
       * latérale identique à ce qu'elle était : un lien simple, sans chevron.
       * La palette, elle, y ajoute les zones — chacune atteignable en une
       * frappe, là où le menu demande deux clics.
       */
      {
        id: 'menus',
        label: 'Menus',
        icon: 'i-lucide-menu',
        to: '/menus',
        adminOnly: true,
        enfantsHorsMenu: true,
        children: zonesDeMenu,
      },
      /**
       * Les associations ont leur propre entrée plutôt qu'un sous-menu de
       * « Menus » : elles alimentent la barre fédération *et* les logos du pied
       * de page, deux zones distinctes. Les ranger sous une seule laisserait
       * croire qu'elles n'en concernent qu'une.
       */
      { id: 'associations', label: 'Associations régionales', icon: 'i-lucide-building-2', to: '/associations' },
      { id: 'redirections', label: 'Redirections', icon: 'i-lucide-signpost', to: '/redirects', adminOnly: true },
      /**
       * Ouvert à tous : chacun gère ses propres tokens. La vue de supervision,
       * elle, est un onglet de l'écran, réservé aux super-administrateurs.
       */
      { id: 'tokens-api', label: 'IA et MCP', icon: 'i-lucide-key-round', to: '/tokens-api' },
      // Réservé, comme la route, aux superadministrateurs de l'annuaire (#332).
      { id: 'menu-administration', label: 'Menu d\'administration', icon: 'i-lucide-list-tree', to: '/menu-administration', superAdminOnly: true },
      { id: 'sauvegardes', label: 'Sauvegardes', icon: 'i-lucide-database-backup', to: '/sauvegardes', superAdminOnly: true },
      { id: 'seeds', label: 'Seeds', icon: 'i-lucide-database', to: '/seeds', appSuperAdminOnly: true },
    ],
  },
  {
    key: 'ia',
    label: 'Intelligence artificielle',
    accent: 'primary',
    items: [
      /**
       * Trois entrées plutôt qu'une page à onglets : la configuration, les
       * demandes prêtes à l'emploi et le suivi des usages ne se consultent pas
       * dans le même mouvement, et l'écran des usages a besoin de toute sa
       * largeur pour son tableau.
       *
       * L'ordre suit celui de la mise en service : on règle quel modèle répond,
       * puis ce qu'on lui demande, et l'on regarde enfin ce que ça a coûté.
       */
      { id: 'ia-configuration', label: 'Configuration', icon: 'i-lucide-sliders-horizontal', to: '/ia/configuration', adminOnly: true },
      { id: 'ia-prompts', label: 'Pré-prompts', icon: 'i-lucide-message-square-quote', to: '/ia/prompts', adminOnly: true },
      { id: 'ia-usages', label: 'Usages', icon: 'i-lucide-chart-line', to: '/ia/usages', adminOnly: true },
    ],
  },
]

// --- Personnalisation (#332) ---

/** Ce que l'API rend pour personnaliser le menu. */
export interface PersonnalisationMenu {
  config: MenuAdministrationConfig
  pagesSysteme: PageSystemeMenu[]
}

/** Groupe où arrive une page système sans entrée dans le code. */
const GROUPE_PAGES_AJOUTEES = 'site'

/** Toutes les cibles de l'arbre, à toute profondeur. */
function cibles(nodes: NavNode[], acc = new Set<string>()): Set<string> {
  for (const node of nodes) {
    if (node.to) acc.add(node.to)
    if (isNavBranch(node)) cibles(node.children, acc)
  }
  return acc
}

/**
 * Une entrée qui mène à une page peut sortir du menu ; un écran fixe
 * (Articles, Médiathèque…) se déplace mais ne se retire pas.
 */
export function entreeRetirable(node: NavNode): boolean {
  return !isNavBranch(node) && (node.id.startsWith(PREFIXE_PAGE) || node.to.startsWith('/pages/'))
}

/** Entrée de premier niveau, avec son groupe d'origine. */
export interface EntreeMenu {
  node: NavNode
  groupe: string
}

/**
 * Toutes les entrées de premier niveau disponibles : celles du code, plus une
 * par page système de la base qui n'a pas déjà la sienne.
 */
export function entreesDisponibles(groups: NavGroup[], pagesSysteme: PageSystemeMenu[]): Map<string, EntreeMenu> {
  const pool = new Map<string, EntreeMenu>()
  for (const group of groups) {
    for (const node of group.items) pool.set(node.id, { node, groupe: group.key })
  }

  const connues = cibles(groups.flatMap(g => g.items))
  for (const page of pagesSysteme) {
    const to = `/pages/${page.slug}`
    if (connues.has(to)) continue
    pool.set(`${PREFIXE_PAGE}${page.slug}`, {
      node: { id: `${PREFIXE_PAGE}${page.slug}`, label: page.title, icon: 'i-lucide-file-text', to },
      groupe: GROUPE_PAGES_AJOUTEES,
    })
  }

  return pool
}

/**
 * Sous-menu qui peut recevoir des entrées : un sous-menu de premier niveau
 * affiché dans la barre latérale. Ceux dont les enfants ne servent qu'à la
 * palette (`enfantsHorsMenu`) n'en reçoivent pas : rien ne s'y verrait.
 */
export function sousMenuReceveur(node: NavNode): node is NavBranch {
  return isNavBranch(node) && !node.enfantsHorsMenu
}

/**
 * Toutes les entrées qu'un écran de réglage peut placer : celles de premier
 * niveau et celles des sous-menus receveurs, avec leur place d'origine.
 */
export interface EntreePlacable {
  node: NavNode
  groupe: string
  /** Sous-menu d'origine, `null` pour une entrée de premier niveau. */
  parent: string | null
}

export function entreesPlacables(groups: NavGroup[], pagesSysteme: PageSystemeMenu[], elements: ElementCree[] = []): Map<string, EntreePlacable> {
  const carte = new Map<string, EntreePlacable>()
  for (const [id, { node, groupe }] of entreesDisponibles(groups, pagesSysteme)) {
    carte.set(id, { node, groupe, parent: null })
    if (sousMenuReceveur(node)) {
      for (const enfant of node.children) carte.set(enfant.id, { node: enfant, groupe, parent: node.id })
    }
  }
  for (const element of elements) carte.set(element.id, { node: noeudCree(element), groupe: GROUPE_PAGES_AJOUTEES, parent: null })
  return carte
}

/** Lien libre ou dossier, tel que le menu le connaît. */
export function noeudCree(element: ElementCree): NavNode {
  if (element.type === 'dossier') return { id: element.id, label: element.label, icon: element.icone, children: [] }
  const externe = estUrlExterne(element.url ?? '')
  return {
    id: element.id,
    label: element.label,
    icon: element.icone,
    to: element.url ?? '/',
    ...(externe ? { externe: true } : {}),
    ...(element.nouvelOnglet ? { nouvelOnglet: true } : {}),
  }
}

/**
 * Les sections effectives : celles du code, puis celles créées à la main,
 * dans l'ordre enregistré, renommées le cas échéant. Une section non citée
 * garde sa place d'origine, en fin de liste.
 */
export function sectionsEffectives(groups: NavGroup[], config: MenuAdministrationConfig): NavGroup[] {
  const toutes: NavGroup[] = [
    ...groups,
    ...(config.sectionsCreees ?? []).map(c => ({ key: c.cle, label: c.label, accent: 'primary' as const, items: [] })),
  ]
  const ordre = config.sections ?? []
  const rang = (cle: string) => {
    const i = ordre.indexOf(cle)
    return i === -1 ? Number.MAX_SAFE_INTEGER : i
  }
  const libelles = config.libelles ?? {}
  return toutes
    .map((g, i) => ({ g, i }))
    .sort((a, b) => rang(a.g.key) - rang(b.g.key) || a.i - b.i)
    .map(({ g }) => ({ ...g, label: libelles[cleLibelleSection(g.key)] ?? g.label }))
}

/**
 * Le menu par défaut, réorganisé selon la configuration enregistrée.
 *
 * Une entrée feuille peut passer d'un sous-menu à un autre, ou du premier
 * niveau à un sous-menu et inversement ; un sous-menu, lui, reste au premier
 * niveau (pas de sous-menu dans un sous-menu). Les groupes gardent leur ordre.
 *
 * Une entrée que la configuration ne place pas — ajoutée au code depuis, ou
 * page système récente — reste à sa place d'origine, en fin de liste : rien
 * de nouveau ne disparaît faute d'avoir été rangé. Une entrée retirée
 * n'apparaît plus, sauf un écran fixe, qui ne se retire pas.
 */
export function appliquerPersonnalisation(groups: NavGroup[], perso?: PersonnalisationMenu | null): NavGroup[] {
  if (!perso) return groups

  const sections = sectionsEffectives(groups, perso.config)
  const clesSections = new Set(sections.map(g => g.key))
  const placables = entreesPlacables(groups, perso.pagesSysteme, perso.config.elements ?? [])
  const { groupes, retirees: retireesBrutes } = perso.config
  const sousMenus = perso.config.sousMenus ?? {}
  const libelles = perso.config.libelles ?? {}
  const icones = perso.config.icones ?? {}

  const retirees = new Set(retireesBrutes.filter((id) => {
    const entree = placables.get(id)
    return entree && entreeRetirable(entree.node)
  }))

  /*
   * Peut porter un sous-menu : toute entrée connue, sauf un sous-menu réservé
   * à la palette. Un sous-menu du code reste un sous-menu même vidé.
   */
  const peutRecevoir = (id: string) => {
    const e = placables.get(id)
    return !!e && !(isNavBranch(e.node) && e.node.enfantsHorsMenu)
  }
  /** Porte des entrées dans la configuration, ou est un sous-menu du code. */
  const porteur = (id: string) => {
    const e = placables.get(id)
    return !!e && (isNavBranch(e.node) || (sousMenus[id]?.length ?? 0) > 0)
  }

  // Place de chaque entrée : un porteur (son id) ou un groupe (`g:<clé>`).
  const place = new Map<string, string>()
  for (const [parent, ids] of Object.entries(sousMenus)) {
    if (!peutRecevoir(parent)) continue
    for (const id of ids) {
      // Un seul niveau : ce qui porte des entrées reste au premier niveau.
      if (id === parent || !placables.has(id) || porteur(id) || place.has(id)) continue
      place.set(id, parent)
    }
  }
  for (const [cle, ids] of Object.entries(groupes)) {
    // Section supprimée : ses entrées retrouvent leur place d'origine.
    if (!clesSections.has(cle)) continue
    for (const id of ids) {
      if (!placables.has(id) || place.has(id)) continue
      place.set(id, `g:${cle}`)
    }
  }
  for (const [id, e] of placables) {
    if (place.has(id)) continue
    // Un enfant du code dont le parent est devenu enfant ailleurs : impossible,
    // un porteur reste au premier niveau. Sa place d'origine tient donc.
    place.set(id, e.parent ?? `g:${e.groupe}`)
  }
  // Un porteur placé dans un sous-menu par sa place d'origine remonte au
  // premier niveau, dans sa section.
  for (const [id, e] of placables) {
    if (porteur(id) && !place.get(id)!.startsWith('g:')) place.set(id, `g:${e.groupe}`)
  }

  /** Les entrées d'un lieu, dans l'ordre enregistré puis celui d'origine. */
  const contenu = (lieu: string, ordre: string[]) => {
    const ids: string[] = []
    for (const id of ordre) if (place.get(id) === lieu && !ids.includes(id) && !retirees.has(id)) ids.push(id)
    for (const [id] of placables) if (place.get(id) === lieu && !ids.includes(id) && !retirees.has(id)) ids.push(id)
    return ids
  }

  const construire = (id: string): NavNode => {
    const node = placables.get(id)!.node
    const label = libelles[id] ?? node.label
    const icon = icones[id] ?? node.icon
    if (isNavBranch(node) && node.enfantsHorsMenu) return { ...node, label, icon, children: renommer(node.children, libelles) }

    const children = contenu(id, sousMenus[id] ?? []).map(construire)
    // Une entrée simple qui reçoit des entrées devient un sous-menu, et garde
    // son lien : la barre latérale l'ouvre au clic, la flèche la déplie. Un
    // dossier vide reste un sous-menu vide, que le filtre par rôle masque.
    if (!isNavBranch(node) && !children.length) return { ...node, label, icon }
    return { ...node, label, icon, children }
  }

  return sections.map(group => ({
    ...group,
    items: contenu(`g:${group.key}`, groupes[group.key] ?? []).map(construire),
  }))
}

/** Applique les libellés renommés, à toute profondeur. */
function renommer(nodes: NavNode[], libelles: Record<string, string>): NavNode[] {
  return nodes.map((node) => {
    const label = libelles[node.id] ?? node.label
    return isNavBranch(node)
      ? { ...node, label, children: renommer(node.children, libelles) }
      : { ...node, label }
  })
}


// --- Filtre par rôle ---

/** Un nœud dont la session ne porte pas le rôle disparaît, avec sa descendance. */
function roleAutorise(node: NavRoles, droits: NavCapabilities): boolean {
  return (!node.adminOnly || droits.isAdmin)
    && (!node.superAdminOnly || droits.isGlobalSuperAdmin)
    && (!node.appSuperAdminOnly || droits.isSuperAdmin)
}

/**
 * L'arborescence effective : les entrées hors des droits de la session
 * disparaissent, à toute profondeur.
 *
 * Masquer n'est pas protéger — `/seeds` porte le middleware `superadmin-app`,
 * `/sauvegardes` refuse quiconque n'est pas superadministrateur de l'annuaire,
 * et les routes qu'elles appellent vérifient le rôle côté serveur. On évite
 * seulement de proposer une destination qui répondrait 403.
 *
 * Une branche vidée de toutes ses feuilles disparaît à son tour : elle ne mène
 * plus nulle part, et l'afficher laisserait un sous-menu qui ne s'ouvre sur
 * rien.
 */
function filtrerNoeuds(nodes: NavNode[], droits: NavCapabilities): NavNode[] {
  const retenus: NavNode[] = []

  for (const node of nodes) {
    if (!roleAutorise(node, droits)) continue

    if (!isNavBranch(node)) {
      retenus.push(node)
      continue
    }

    const children = filtrerNoeuds(node.children, droits)
    if (children.length) retenus.push({ ...node, children })
  }

  return retenus
}

/**
 * L'arborescence filtrée, groupes vides retirés.
 *
 * C'est ce que consomme la palette : elle voit tout ce à quoi la session a
 * droit, zones de menu comprises.
 */
export function navGroupsForCapabilities(droits: NavCapabilities, perso?: PersonnalisationMenu | null): NavGroup[] {
  return appliquerPersonnalisation(navGroups, perso)
    .map(group => ({ ...group, items: filtrerNoeuds(group.items, droits) }))
    .filter(group => group.items.length)
}

/** Les branches marquées `enfantsHorsMenu` redeviennent des liens simples. */
function elaguerHorsMenu(nodes: NavNode[]): NavNode[] {
  return nodes.map((node) => {
    if (!isNavBranch(node)) return node
    if (node.enfantsHorsMenu) {
      const { children: _children, enfantsHorsMenu: _drapeau, ...reste } = node
      // Une branche `enfantsHorsMenu` porte toujours un `to` — sans lui, l'élaguer
      // laisserait une entrée qui ne mène nulle part. Le type ne peut pas l'exiger,
      // le repli sur `#` évite un lien vide si la règle venait à être enfreinte.
      return { ...reste, to: node.to ?? '#' } satisfies NavLeaf
    }
    return { ...node, children: elaguerHorsMenu(node.children) }
  })
}

/**
 * L'arborescence telle que la barre latérale l'affiche.
 *
 * Même filtre par rôle que la palette — c'est tout l'intérêt de la source
 * unique — mais amputée des enfants réservés à la palette.
 */
export function menuGroupsForCapabilities(droits: NavCapabilities, perso?: PersonnalisationMenu | null): NavGroup[] {
  return navGroupsForCapabilities(droits, perso)
    .map(group => ({ ...group, items: elaguerHorsMenu(group.items) }))
}

// --- Aplatissement ---

/** Une destination, avec le chemin qui l'a menée jusqu'à elle. */
export interface NavDestination {
  id: string
  label: string
  icon: string
  to: string
  /** Les libellés des ancêtres, du groupe jusqu'au parent direct. */
  ancetres: string[]
  /** « Site › Législation », vide pour une entrée de premier niveau. */
  chemin: string
  /**
   * Libellé et chemin réunis, sans diacritiques — pour la recherche seule.
   *
   * Fuse ne normalise pas les accents : sans ce champ, `media` ne trouve pas
   * « Médiathèque » ni `theme` « Thèmes ». Dans un CMS français, personne ne
   * tape ses accents pour aller vite.
   */
  recherche: string
  /** Lien libre hors de l'administration, à ouvrir comme tel (#343). */
  externe?: boolean
  nouvelOnglet?: boolean
}

export const SEPARATEUR_CHEMIN = ' › '

/** Retire les diacritiques : « Médiathèque » devient « Mediatheque ». */
export function sansDiacritiques(valeur: string): string {
  return valeur.normalize('NFD').replace(/\p{Diacritic}/gu, '')
}

/**
 * Aplatit un sous-arbre en destinations, chacune portant son chemin complet.
 *
 * Récursif sans borne de profondeur : c'est ce qui fait que le libellé d'un
 * niveau intermédiaire vaut comme terme de recherche pour ses feuilles, aussi
 * loin qu'elles soient rangées.
 *
 * Une branche qui porte aussi un `to` est à la fois destination et chemin : on
 * l'émet, puis on descend dans ses enfants.
 */
function aplatirNoeuds(nodes: NavNode[], ancetres: string[]): NavDestination[] {
  const destinations: NavDestination[] = []

  for (const node of nodes) {
    if (node.to) {
      const chemin = ancetres.join(SEPARATEUR_CHEMIN)
      destinations.push({
        id: node.id,
        label: node.label,
        icon: node.icon,
        to: node.to,
        ancetres,
        chemin,
        recherche: sansDiacritiques(`${node.label} ${chemin}`),
        ...(!isNavBranch(node) && node.externe ? { externe: true } : {}),
        ...(!isNavBranch(node) && node.nouvelOnglet ? { nouvelOnglet: true } : {}),
      })
    }

    if (isNavBranch(node)) {
      destinations.push(...aplatirNoeuds(node.children, [...ancetres, node.label]))
    }
  }

  return destinations
}

/**
 * Toutes les destinations de l'arborescence, à plat, dans l'ordre du menu.
 *
 * Le groupe est le premier ancêtre : taper `site` remonte tout ce que le groupe
 * Site contient, à n'importe quelle profondeur.
 */
export function flattenNavigation(groups: NavGroup[]): NavDestination[] {
  return groups.flatMap(group => aplatirNoeuds(group.items, [group.label]))
}

/**
 * La cible du menu à surligner pour un chemin donné.
 *
 * Correspondance exacte d'abord. Sinon, la cible dont le chemin est un préfixe
 * — et, parmi plusieurs, **la plus longue** : `/legislation/themes/reorder`
 * commence par `/legislation/themes` comme par `/legislation`, et seul
 * « Thèmes » doit s'allumer, pas « Fiches » en même temps (#200).
 *
 * Une page absente du menu laisse ainsi son entrée parente active — une page
 * ordinaire allume « Pages » —, alors qu'une page pilier, qui a sa propre
 * entrée (`/pages/contact`), n'allume qu'elle.
 */
export function activeMenuTarget(path: string, targets: readonly string[]): string | null {
  if (targets.includes(path)) return path

  let meilleure: string | null = null
  for (const to of targets) {
    if (to === '/' || !path.startsWith(to + '/')) continue
    if (!meilleure || to.length > meilleure.length) meilleure = to
  }

  return meilleure
}
